<template>
  <div>
    <div class="row mb-4">
      <div :class="form.is_foreign ? 'col-md-4 order-md-1' : 'col-md-4'">
        <label class="fs-6 fw-bold mb-2 required">Paciente</label>
        <SearchPatient
          v-model="form.sched_patient_name"
          :disabled="readonly"
          @onClick="onPatientSelected"
        />
      </div>
      <div :class="form.is_foreign ? 'col-md-4 order-md-1' : 'col-md-2'">
        <PatientIdentityFields :form="form" :show-nationality="false" cpf-field="sched_patient_cpf" :disabled="readonly" />
      </div>
      <div :class="form.is_foreign ? 'col-md-4 order-md-3 mt-3' : 'col-md-2'">
        <label class="fs-6 fw-bold mb-2">Nascimento</label>
        <input type="tel" class="form-control form-control-solid" v-mask="'##/##/####'" v-model="form.sched_patient_birthdate" placeholder="DD/MM/YYYY" :disabled="readonly">
      </div>
      <div :class="form.is_foreign ? 'col-md-4 order-md-2' : 'col-md-2'">
        <label class="fs-6 fw-bold mb-2">Responsável</label>
        <input type="text" class="form-control form-control-solid" v-model="form.patient_resp_name" placeholder="Nome do responsável" :disabled="readonly">
      </div>
      <div :class="form.is_foreign ? 'col-md-4 order-md-3 mt-3' : 'col-md-2'">
        <label class="fs-6 fw-bold mb-2">Telefone</label>
        <input type="text" class="form-control form-control-solid" v-mask="'(##) #####-####'" v-model="form.sched_patient_phone" :disabled="readonly">
      </div>
      <div v-if="form.is_foreign" class="col-md-4 order-md-3 mt-3">
        <PatientNationalityField :form="form" :disabled="readonly" />
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-md-4">
        <label class="fs-6 fw-bold mb-2 required">Forma de pagamento</label>
        <select class="form-control form-control-solid" v-model="form.id_paymeth" :disabled="readonly">
          <option :value="null">--Selecione--</option>
          <option v-for="pm in paymentMethods" :key="pm.id_paymeth" :value="pm.id_paymeth">{{ pm.paymeth_name }}</option>
        </select>
      </div>
      <div class="col-md-4" v-show="allowInsurance">
        <label class="fs-6 fw-bold mb-2 required">Convênio</label>
        <Dropdown
          :options="insurances"
          :filter="true"
          appendTo="self"
          :baseZIndex="1100"
          v-model="form.id_insurance"
          optionLabel="insurance_name"
          optionValue="id_insurance"
          :filter-fields="['insurance_name', 'trading_name']"
          showClear
          empty-message="Nenhuma opção"
          empty-filter-message="Nenhuma opção encontrada"
          class="form-control form-control-solid w-100"
          placeholder="Selecione o convênio"
          style="display: flex; padding: 0.25rem .75rem"
          :disabled="readonly"
        >
          <template #option="slotProps">
            <div class="flex align-items-center text-truncate" style="max-width: 350px;">
              <div v-if="slotProps.option.trading_name">
                {{ slotProps.option.trading_name }}
                -
                {{ slotProps.option.insurance_name }}
              </div>
              <div v-else>
                {{ slotProps.option.insurance_name }}
              </div>
            </div>
          </template>
        </Dropdown>
      </div>
      <div class="col-md-4">
        <label class="fs-6 fw-bold mb-2 required">Data de início</label>
        <input type="tel" class="form-control form-control-solid" v-mask="'##/##/####'" v-model="form.start_date" placeholder="DD/MM/YYYY" :disabled="readonly">
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-12">
        <div class="form-check form-check-custom form-check-solid">
          <input
            class="form-check-input"
            type="checkbox"
            id="program_send_confirmations"
            v-model="form.send_confirmations"
            :disabled="readonly"
          >
          <label class="form-check-label" for="program_send_confirmations">
            Enviar confirmação automática dos agendamentos
            <span class="text-muted fs-8 d-block">
              Se marcado, o paciente recebe as notificações de confirmação como nos agendamentos normais.
            </span>
          </label>
        </div>
      </div>
    </div>

    <div class="border rounded p-4 mb-4">
      <div class="mb-3">
        <h5 class="fw-bolder mb-1">Profissionais</h5>
        <div class="text-muted fs-7">Cada profissional pode ter a própria recorrência ou apenas um agendamento.</div>
      </div>

      <div
        v-for="(slot, index) in slots"
        :key="slot._key"
        class="border rounded p-3 mb-3 program-slot-card"
      >
        <div class="d-flex justify-content-between align-items-center mb-3">
          <span class="fw-bold fs-7 text-muted">Profissional {{ index + 1 }}</span>
          <div v-if="!readonly">
            <button type="button" class="btn btn-sm btn-light-danger" title="Remover" @click="removeSlot(index)" :disabled="slots.length === 1">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-md-3">
            <label class="fs-7 fw-bold mb-1 required">Profissional</label>
            <Dropdown
              :options="professionals"
              :filter="true"
              appendTo="self"
              :baseZIndex="1100"
              v-model="slot.id_prof"
              optionLabel="prof_name"
              optionValue="id_prof"
              @change="onSlotProfChange(slot)"
              empty-message="Nenhuma opção"
              empty-filter-message="Nenhuma opção encontrada"
              class="form-control form-control-solid form-control-sm w-100"
              placeholder="Profissional"
              style="display: flex; padding: 0.25rem .5rem"
              :disabled="readonly"
            />
          </div>
          <div class="col-md-4">
            <label class="fs-7 fw-bold mb-1 required">Especialidade</label>
            <Dropdown
              :options="slot.specialties"
              :filter="true"
              appendTo="self"
              :baseZIndex="1100"
              v-model="slot.id_spec"
              optionLabel="spec_name"
              optionValue="id_spec"
              @change="onSlotSpecChange(slot)"
              empty-message="Nenhuma opção"
              empty-filter-message="Nenhuma opção encontrada"
              class="form-control form-control-solid form-control-sm w-100"
              placeholder="Especialidade"
              style="display: flex; padding: 0.25rem .5rem"
              :disabled="readonly"
            />
          </div>
          <div class="col-md-3">
            <label class="fs-7 fw-bold mb-1 required">1ª consulta</label>
            <Calendar
              v-model="slot.first_date_obj"
              dateFormat="dd/mm/yy"
              :manualInput="false"
              showIcon
              appendTo="self"
              :baseZIndex="1100"
              class="form-control form-control-solid form-control-sm w-100 program-slot-calendar"
              placeholder="Selecione"
              :disabled="readonly || !canLoadSlotDates(slot)"
              @show="() => loadSlotDatesAvailability(slot)"
              @dateSelect="(date) => onSlotDateSelect(slot, date)"
              @monthChange="(month) => onSlotMonthChange(slot, month)"
            >
              <template #date="slotProps">
                <div v-if="dateIsAvailability(slot, slotProps.date)">
                  <div
                    :style="{
                      color: getTextColor(slot, slotProps.date),
                      backgroundColor: getBackgroundColor(slot, slotProps.date),
                      padding: '1px',
                      borderRadius: '50%',
                      width: '30px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }"
                    :title="`${getEventRate(slot, slotProps.date)}%`"
                  >
                    <strong>{{ slotProps.date.day }}</strong>
                  </div>
                </div>
                <div v-else>
                  <i style="pointer-events: none; cursor: not-allowed">{{ slotProps.date.day }}</i>
                </div>
              </template>
            </Calendar>
            <div class="text-muted fs-8 mt-1" v-if="slotWeekDayLabel(slot)">{{ slotWeekDayLabel(slot) }}</div>
          </div>
          <div class="col-md-2">
            <label class="fs-7 fw-bold mb-1 required">Horário</label>
            <select
              class="form-control form-control-solid form-control-sm"
              v-model="slot.sched_time"
              @change="onSlotTimeChange(slot)"
              :disabled="readonly || !slot.first_date"
            >
              <option :value="null">{{ slot.first_date ? '--' : 'Selecione a data' }}</option>
              <option v-for="t in slot.times" :key="t.available_time || t" :value="t.available_time || t">{{ t.available_time || t }}</option>
            </select>
          </div>
        </div>

        <div class="row g-2 mt-1 align-items-end">
          <div class="col-md-2">
            <label class="fs-7 fw-bold mb-1 required">Recorrência</label>
            <select class="form-control form-control-solid form-control-sm" v-model="slot.has_recurrence" :disabled="readonly">
              <option :value="true">Sim</option>
              <option :value="false">Não</option>
            </select>
          </div>
          <template v-if="slot.has_recurrence">
            <div class="col-md-3">
              <label class="fs-7 fw-bold mb-1 required">Tipo de recorrência</label>
              <select class="form-control form-control-solid form-control-sm" v-model="slot.tp_recurrence" @change="onSlotTpRecurrenceChange(slot)" :disabled="readonly">
                <option value="W">Semanal: toda {{ slotWeekDayLabel(slot) || 'data escolhida' }}</option>
                <option value="M">Segunda a sexta</option>
                <option value="A">Todos os dias</option>
                <option value="P">Personalizada</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="fs-7 fw-bold mb-1 required">Máx. sessões</label>
              <div class="input-group input-group-sm">
                <input type="number" min="1" class="form-control form-control-solid form-control-sm" v-model.number="slot.max_occurrences" :disabled="readonly">
                <button v-if="!readonly" type="button" class="schedule-editor__button program-slot-preview" title="Pré-visualizar datas" aria-label="Pré-visualizar datas" @click="previewSlot(slot, index)">
                  <i class="bi bi-eye"></i>
                </button>
              </div>
            </div>
            <template v-if="slot.tp_recurrence === 'P'">
              <div class="col-md-2">
                <label class="fs-7 fw-bold mb-1">Repetir a cada</label>
                <input type="number" min="1" class="form-control form-control-solid form-control-sm" v-model.number="slot.interval" :disabled="readonly">
              </div>
              <div class="col-md-3">
                <label class="fs-7 fw-bold mb-1">Frequência</label>
                <select class="form-control form-control-solid form-control-sm" v-model="slot.frequency" :disabled="readonly">
                  <option value="daily">{{ slot.interval > 1 ? 'Dias' : 'Dia' }}</option>
                  <option value="weekly">{{ slot.interval > 1 ? 'Semanas' : 'Semana' }}</option>
                  <option value="monthly">{{ slot.interval > 1 ? 'Meses' : 'Mês' }}</option>
                </select>
              </div>
            </template>
          </template>
        </div>

        <div v-if="slot.has_recurrence && slot.tp_recurrence === 'P' && slot.frequency === 'weekly'" class="d-flex flex-wrap gap-1 mt-2">
          <template v-for="day in weekDays" :key="`${slot._key}-${day.value}`">
            <input
              type="checkbox"
              class="btn-check"
              :id="`${slot._key}-${day.value}`"
              :value="day.value"
              v-model="slot.days_of_week"
              :disabled="readonly"
            >
            <label
              class="btn btn-outline-primary border-primary"
              :for="`${slot._key}-${day.value}`"
              style="padding: calc(0.45rem + 1px) calc(0.65rem + 1px);"
            >
              {{ day.short }}
            </label>
          </template>
        </div>
      </div>

      <button
        v-if="!readonly"
        type="button"
        class="btn btn-sm btn-outline btn-outline-dashed btn-outline-primary w-100"
        @click="addSlot"
      >
        + Adicionar profissional
      </button>
    </div>

    <div class="row mb-4" v-if="hasAnyRecurrence">
      <div class="col-md-8">
        <div class="form-check form-check-custom form-check-solid">
          <input
            class="form-check-input"
            type="checkbox"
            id="program_notify_sessions_ending"
            v-model="form.notify_sessions_ending"
            :disabled="readonly"
          >
          <label class="form-check-label" for="program_notify_sessions_ending">
            Avisar o paciente quando as sessões estiverem acabando
            <span class="text-muted fs-8 d-block">
              Enviado por WhatsApp e e-mail, orientando a solicitar novas sessões ao convênio ou a
              combinar a contratação com a clínica.
            </span>
          </label>
        </div>
      </div>
      <div class="col-md-4" v-if="form.notify_sessions_ending">
        <label class="fs-6 fw-bold mb-2" for="program_sessions_ending_threshold">Avisar quando restarem</label>
        <div class="input-group">
          <input
            type="number"
            min="1"
            max="20"
            class="form-control form-control-solid"
            id="program_sessions_ending_threshold"
            v-model.number="form.sessions_ending_threshold"
            :disabled="readonly"
          >
          <span class="input-group-text">sessão(ões)</span>
        </div>
      </div>
    </div>

    <ScheduleProgramSessionsCard
      v-if="readonly && programId"
      :program-id="programId"
      :summary="sessionsSummary"
      :threshold="form.sessions_ending_threshold"
      :notice-enabled="form.notify_sessions_ending"
      @sent="loadProgram(programId)"
    />

    <div class="alert alert-secondary mb-4" v-if="summaryText && !readonly">
      {{ summaryText }}
    </div>

    <div class="schedule-editor__footer">
      <button type="button" class="schedule-editor__button" :disabled="submitting || loadingProgram" @click="printPreview">
        <i class="bi bi-printer me-1"></i>
        Ver / imprimir
      </button>
      <button
        v-if="!readonly"
        type="button"
        class="schedule-editor__button schedule-editor__button--primary"
        :disabled="submitting"
        @click="submit"
      >
        {{ submitting ? 'Salvando...' : 'Salvar e agendar tudo' }}
      </button>
    </div>
  </div>
</template>

<script>
import { patientIdentity, identityError, isPatientDeceased } from '../../helpers/patientIdentity';
import { mask } from 'vue-the-mask';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import SearchPatient from '../../components/SearchPatient';
import http from '../../helpers/http';
import Alert from '../../helpers/alert';
import Download from '../../helpers/download';
import Loader from '../../helpers/loader';
import ScheduleProgramSessionsCard from './ScheduleProgramSessionsCard.vue';

const WEEK_DAYS = [
  { value: 'sunday', label: 'Domingo', short: 'Dom' },
  { value: 'monday', label: 'Segunda', short: 'Seg' },
  { value: 'tuesday', label: 'Terça', short: 'Ter' },
  { value: 'wednesday', label: 'Quarta', short: 'Qua' },
  { value: 'thursday', label: 'Quinta', short: 'Qui' },
  { value: 'friday', label: 'Sexta', short: 'Sex' },
  { value: 'saturday', label: 'Sábado', short: 'Sab' },
];

function emptySlot() {
  return {
    _key: `${Date.now()}-${Math.random()}`,
    id_prof: null,
    id_spec: null,
    first_date: null,
    first_date_obj: null,
    has_recurrence: true,
    tp_recurrence: 'W',
    frequency: 'weekly',
    interval: 1,
    days_of_week: [],
    max_occurrences: 16,
    week_day: null,
    sched_time: null,
    interval_minutes: null,
    id_avail: null,
    id_place_location: null,
    specialties: [],
    times: [],
    datesAvailability: [],
    paymentMethodIds: null,
    insuranceIds: null,
  };
}

export default {
  name: 'ModalScheduleProgram',
  components: { SearchPatient, Dropdown, Calendar, ScheduleProgramSessionsCard },
  directives: { mask },
  props: {
    readonly: { type: Boolean, default: false },
    programId: { type: [Number, String], default: null },
  },
  emits: ['saved'],
  data() {
    return {
      weekDays: WEEK_DAYS,
      paymentMethods: [],
      insurances: [],
      submitting: false,
      loadingProgram: false,
      form: {
        sched_patient_name: '',
        sched_patient_cpf: '',
        sched_patient_phone: '',
        sched_patient_birthdate: null,
        patient_resp_name: '',
        id_paymeth: null,
        id_insurance: null,
        start_date: moment().format('DD/MM/YYYY'),
        program_type: 'multiprofessional',
        program_name: null,
        program_obs: null,
        send_confirmations: true,
        notify_sessions_ending: false,
        sessions_ending_threshold: 2,
      },
      sessionsSummary: {},
      slots: [emptySlot()],
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    professionals() {
      return this.$store.state.auth.professionals || [];
    },
    allowInsurance() {
      if (!this.form.id_paymeth) return false;
      const paymentMethod = this.paymentMethods.find((p) => p.id_paymeth === this.form.id_paymeth);
      return !!paymentMethod?.is_insurance;
    },
    hasAnyRecurrence() {
      return this.slots.some((slot) => slot.has_recurrence);
    },
    summaryText() {
      if (!this.slots.length) return '';
      const total = this.slots.reduce(
        (sum, slot) => sum + (slot.has_recurrence ? (Number(slot.max_occurrences) || 0) : 1),
        0,
      );
      if (!total) return `${this.slots.length} profissional(is). Informe a quantidade máxima de sessões.`;
      return `Serão criados cerca de ${total} agendamentos no total.`;
    },
  },
  mounted() {
    this.loadPlacePaymentOptions();
    if (this.programId) {
      this.loadProgram(this.programId);
    } else {
      this.slots.forEach((slot) => this.onSlotTpRecurrenceChange(slot));
    }
  },
  watch: {
    hasAnyRecurrence(value) {
      if (!value && !this.readonly) {
        this.form.notify_sessions_ending = false;
      }
    },
    programId(value) {
      if (value) {
        this.loadProgram(value);
      }
    },
    'form.id_paymeth'() {
      if (this.readonly || this.loadingProgram) return;
      if (!this.allowInsurance) {
        this.form.id_insurance = null;
      }
      this.slots.forEach((slot) => {
        this.clearSlotDateAndTimes(slot);
        this.loadSlotDatesAvailability(slot);
        this.warnSlotPayment(slot, 'paymeth');
      });
    },
    'form.id_insurance'() {
      if (this.readonly || this.loadingProgram) return;
      this.slots.forEach((slot) => {
        this.clearSlotDateAndTimes(slot);
        this.loadSlotDatesAvailability(slot);
        this.warnSlotPayment(slot, 'insurance');
      });
    },
  },
  methods: {
    date: moment,
    loadProgram(programId) {
      if (!programId || !this.place?.id_place) return;

      this.loadingProgram = true;
      return http.get(`/places/${this.place.id_place}/schedules/programs/${programId}`)
        .then((response) => {
          this.applyProgram(response.data);
        })
        .catch((e) => Alert.toastError(identityError(e)))
        .finally(() => {
          this.loadingProgram = false;
        });
    },
    applyProgram(program) {
      const patient = program.patient || {};
      this.form = {
        ...this.form,
        sched_patient_name: patient.patient_name || '',
        ...patientIdentity(patient),
        sched_patient_cpf: patient.patient_cpf || '',
        sched_patient_phone: patient.patient_phone || '',
        sched_patient_birthdate: patient.patient_birthdate_br
          || (patient.patient_birthdate ? moment(patient.patient_birthdate).format('DD/MM/YYYY') : null),
        patient_resp_name: patient.patient_resp_name || '',
        id_paymeth: program.id_paymeth,
        id_insurance: program.id_insurance,
        start_date: program.start_date ? moment(program.start_date).format('DD/MM/YYYY') : null,
        program_type: program.program_type || 'multiprofessional',
        program_name: program.program_name,
        program_obs: program.program_obs,
        send_confirmations: !!program.send_confirmations,
        notify_sessions_ending: program.notify_sessions_ending !== false,
        sessions_ending_threshold: program.sessions_ending_threshold ?? 2,
      };

      this.sessionsSummary = program.sessions_summary ?? {};

      if (program.payment_method && !this.paymentMethods.find((p) => p.id_paymeth === program.id_paymeth)) {
        this.paymentMethods.push(program.payment_method);
      }
      if (program.insurance && !this.insurances.find((i) => i.id_insurance === program.id_insurance)) {
        this.insurances.push(program.insurance);
      }

      this.slots = (program.slots || []).map((slot) => {
        const recurrence = slot.recurrence || {};
        const firstSchedule = (slot.schedules || [])[0];
        const firstDate = firstSchedule?.sched_date
          || recurrence.start_date
          || program.start_date;
        const firstDateMoment = firstDate ? moment(firstDate) : null;
        const time = (slot.sched_time || firstSchedule?.sched_time || '').toString().slice(0, 5);

        return {
          ...emptySlot(),
          id_prof: slot.id_prof,
          id_spec: slot.id_spec,
          first_date: firstDateMoment?.isValid() ? firstDateMoment.format('DD/MM/YYYY') : null,
          first_date_obj: firstDateMoment?.isValid() ? firstDateMoment.toDate() : null,
          has_recurrence: !!(slot.sched_recur_id || slot.recurrence),
          tp_recurrence: recurrence.tp_recurrence || 'W',
          frequency: recurrence.frequency || 'weekly',
          interval: recurrence.interval || 1,
          days_of_week: recurrence.days_of_week || [],
          max_occurrences: recurrence.max_occurrences || (slot.schedules || []).length || 1,
          week_day: slot.week_day,
          sched_time: time || null,
          interval_minutes: slot.interval_minutes,
          id_avail: slot.id_avail,
          id_place_location: slot.id_place_location,
          specialties: slot.specialty ? [slot.specialty] : [],
          times: time ? [{ available_time: time }] : [],
        };
      });

      if (!this.slots.length) {
        this.slots = [emptySlot()];
      }
    },
    onPatientSelected(patient) {
      if (!patient || this.readonly) return;
      if (isPatientDeceased(patient)) {
        Alert.toastError('Não é possível agendar para paciente em óbito.');
        return;
      }
      this.form.sched_patient_name = patient.patient_name || patient.name || '';
      Object.assign(this.form, patientIdentity(patient));
      this.form.sched_patient_cpf = patient.patient_cpf || patient.cpf || '';
      this.form.sched_patient_phone = patient.patient_phone || patient.phone || '';
      this.form.sched_patient_birthdate = patient.patient_birthdate_br || patient.patient_birthdate || null;
      this.form.patient_resp_name = patient.patient_resp_name || '';
    },
    addSlot() {
      if (this.readonly) return;
      const slot = emptySlot();
      this.slots.push(slot);
      this.onSlotTpRecurrenceChange(slot);
    },
    removeSlot(index) {
      if (this.readonly || this.slots.length === 1) return;

      const slot = this.slots[index];
      const prof = this.professionals.find((p) => p.id_prof === slot.id_prof);
      const label = prof?.prof_name ? `<strong>${prof.prof_name}</strong>` : `o profissional ${index + 1}`;

      Swal.fire({
        title: 'Remover profissional?',
        html: `Tem certeza que deseja remover ${label} do programa?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          this.slots.splice(index, 1);
        }
      });
    },
    calendarDayMoment(date) {
      if (date instanceof Date) return moment(date);
      if (date?.year != null && date?.month != null && date?.day != null) {
        return moment({ year: date.year, month: date.month, day: date.day });
      }
      return moment(date);
    },
    slotWeekDay(slot) {
      const date = moment(slot.first_date, 'DD/MM/YYYY', true);
      if (!date.isValid()) return null;
      return date.locale('en').format('dddd').toLowerCase();
    },
    slotWeekDayLabel(slot) {
      const weekDay = this.slotWeekDay(slot);
      if (!weekDay) return '';
      return WEEK_DAYS.find((d) => d.value === weekDay)?.label || '';
    },
    canLoadSlotDates(slot) {
      return !!(slot.id_prof && slot.id_spec && this.form.id_paymeth);
    },
    clearSlotDateAndTimes(slot) {
      slot.first_date = null;
      slot.first_date_obj = null;
      slot.week_day = null;
      slot.sched_time = null;
      slot.times = [];
      slot.interval_minutes = null;
      slot.id_avail = null;
      slot.id_place_location = null;
    },
    onSlotProfChange(slot) {
      if (this.readonly) return;
      slot.id_spec = null;
      slot.specialties = [];
      slot.datesAvailability = [];
      slot.paymentMethodIds = null;
      slot.insuranceIds = null;
      this.clearSlotDateAndTimes(slot);
      if (!slot.id_prof) return;

      http.get(`/places/${this.place.id_place}/professionals/${slot.id_prof}/specialties`).then((response) => {
        slot.specialties = response.data || [];
      });
      this.loadSlotPaymentOptions(slot).then(() => this.warnSlotPayment(slot));
    },
    loadSlotPaymentOptions(slot) {
      const idProf = slot.id_prof;
      const basePath = `/places/${this.place.id_place}/professionals/${idProf}`;

      return Promise.all([
        http.get(`${basePath}/payment-methods`, { params: { from_calendar: true } }),
        http.get(`${basePath}/insurances`),
      ]).then(([paymentMethods, insurances]) => {
        if (slot.id_prof !== idProf) return;
        slot.paymentMethodIds = (paymentMethods.data || [])
          .filter((payMethod) => payMethod.enabled_to_prof_place === 1)
          .map((payMethod) => payMethod.id_paymeth);
        slot.insuranceIds = (insurances.data || [])
          .filter((insurance) => insurance.enabled_to_prof_place === 1)
          .map((insurance) => insurance.id_insurance);
      });
    },
    slotPaymentError(slot, only = null) {
      if (!slot.id_prof) return null;
      const name = this.professionals.find((p) => p.id_prof === slot.id_prof)?.prof_name || 'O profissional selecionado';

      if (only !== 'insurance' && this.form.id_paymeth && slot.paymentMethodIds
        && !slot.paymentMethodIds.includes(this.form.id_paymeth)) {
        return `${name} não atende na forma de pagamento selecionada.`;
      }
      if (only !== 'paymeth' && this.allowInsurance && this.form.id_insurance && slot.insuranceIds
        && !slot.insuranceIds.includes(this.form.id_insurance)) {
        return `${name} não atende o convênio selecionado.`;
      }
      return null;
    },
    warnSlotPayment(slot, only = null) {
      const error = this.slotPaymentError(slot, only);
      if (error) Alert.toastWarning(error);
    },
    onSlotSpecChange(slot) {
      if (this.readonly) return;
      slot.datesAvailability = [];
      this.clearSlotDateAndTimes(slot);
      this.loadSlotDatesAvailability(slot);
    },
    onSlotDateSelect(slot, selectedDate) {
      if (this.readonly) return;
      const selected = this.calendarDayMoment(selectedDate);
      const searchDate = selected.format('YYYY-MM-DD');
      const available = (slot.datesAvailability || []).some((event) => event.available_date === searchDate);
      if (!available) {
        slot.first_date_obj = null;
        slot.first_date = null;
        slot.sched_time = null;
        slot.times = [];
        Alert.toastError('Selecione uma data com disponibilidade.');
        return;
      }

      slot.first_date_obj = selectedDate;
      slot.first_date = selected.format('DD/MM/YYYY');
      slot.week_day = selected.locale('en').format('dddd').toLowerCase();
      slot.sched_time = null;
      slot.times = [];
      slot.interval_minutes = null;
      slot.id_avail = null;
      this.onSlotTpRecurrenceChange(slot);
      this.loadSlotTimes(slot);
    },
    onSlotMonthChange(slot, monthMeta) {
      if (this.readonly) return;
      const startDate = `${monthMeta.year}-${String(monthMeta.month).padStart(2, '0')}-01`;
      this.loadSlotDatesAvailability(slot, startDate);
    },
    onSlotTpRecurrenceChange(slot) {
      const weekDay = this.slotWeekDay(slot);
      if (slot.tp_recurrence === 'A') {
        slot.frequency = 'daily';
        slot.interval = 1;
        slot.days_of_week = WEEK_DAYS.map((d) => d.value);
      } else if (slot.tp_recurrence === 'M') {
        slot.frequency = 'weekly';
        slot.interval = 1;
        slot.days_of_week = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      } else if (slot.tp_recurrence === 'P') {
        slot.frequency = slot.frequency || 'weekly';
        slot.interval = slot.interval || 1;
        if (!slot.days_of_week?.length) {
          slot.days_of_week = [];
        }
      } else {
        slot.tp_recurrence = 'W';
        slot.frequency = 'weekly';
        slot.interval = 1;
        slot.days_of_week = weekDay ? [weekDay] : [];
      }
    },
    loadPlacePaymentOptions() {
      if (!this.place?.id_place) return;

      http.get(`/places/${this.place.id_place}/payment-methods`, {
        params: { from_calendar: true },
      }).then((response) => {
        this.paymentMethods = (response.data ?? []).filter(
          (payMethod) => ['BO', 'PL'].includes(payMethod.allowed_for),
        );
        if (!this.readonly && !this.programId && this.paymentMethods.length === 1) {
          this.form.id_paymeth = this.paymentMethods[0].id_paymeth;
        }
      });
      http.get(`/places/${this.place.id_place}/insurances`).then((response) => {
        this.insurances = response.data?.data ?? response.data ?? [];
      });
    },
    loadSlotDatesAvailability(slot, startDate = null) {
      if (this.readonly || !this.canLoadSlotDates(slot)) {
        slot.datesAvailability = [];
        return;
      }

      http.get('/schedule/availabilities', {
        params: {
          id_place: this.place.id_place,
          id_prof: slot.id_prof,
          id_spec: slot.id_spec,
          id_city: this.place.id_city,
          id_paymeth: this.form.id_paymeth,
          id_insurance: this.form.id_insurance,
          start_date: startDate || moment().format('YYYY-MM-DD'),
          start_end_days: 32,
        },
      }).then((response) => {
        slot.datesAvailability = response.data || [];
      });
    },
    dateIsAvailability(slot, date) {
      const searchDate = this.calendarDayMoment(date).format('YYYY-MM-DD');
      return (slot.datesAvailability || []).some((event) => event.available_date === searchDate);
    },
    getTextColor(slot, date) {
      const searchDate = this.calendarDayMoment(date).format('YYYY-MM-DD');
      return (slot.datesAvailability || []).find((event) => event.available_date === searchDate)?.text_color;
    },
    getEventRate(slot, date) {
      const searchDate = this.calendarDayMoment(date).format('YYYY-MM-DD');
      return (slot.datesAvailability || []).find((event) => event.available_date === searchDate)?.busy_rate;
    },
    getBackgroundColor(slot, date) {
      const searchDate = this.calendarDayMoment(date).format('YYYY-MM-DD');
      return (slot.datesAvailability || []).find((event) => event.available_date === searchDate)?.background_color;
    },
    loadSlotTimes(slot) {
      slot.sched_time = null;
      slot.times = [];
      if (!slot.id_prof || !slot.id_spec || !this.form.id_paymeth || !slot.first_date) return;

      const date = moment(slot.first_date, 'DD/MM/YYYY').format('YYYY/MM/DD');
      http.get('/schedule/availabilities/times', {
        params: {
          id_place: this.place.id_place,
          id_prof: slot.id_prof,
          id_spec: slot.id_spec,
          id_city: this.place.id_city,
          id_paymeth: this.form.id_paymeth,
          id_insurance: this.form.id_insurance,
          available_date: date,
        },
      }).then((response) => {
        slot.times = response.data || [];
      });
    },
    onSlotTimeChange(slot) {
      const selected = (slot.times || []).find((t) => t.available_time === slot.sched_time);
      slot.id_avail = selected?.id_avail ?? null;
      slot.id_place_location = selected?.id_place_location ?? null;
      slot.interval_minutes = selected?.interval_minutes ?? null;
    },
    previewSlot(slot, index) {
      if (!this.validateBase() || !this.validateSlot(slot, index)) return;

      http.post(`/places/${this.place.id_place}/schedules/programs/preview-slot`, {
        ...this.payloadBase(),
        slot: this.slotPayload(slot),
      }).then((response) => {
        const dates = response.data || [];
        let table = '<div style="max-height:300px;overflow:auto;"><table class="table table-bordered"><thead><tr><th>Data</th><th>Hora</th><th>Dia</th></tr></thead><tbody>';
        dates.forEach((item) => {
          table += `<tr><td>${moment(item).format('DD/MM/YYYY')}</td><td>${slot.sched_time}</td><td>${moment(item).locale('pt-br').format('dddd')}</td></tr>`;
        });
        table += '</tbody></table></div>';
        Swal.fire({
          title: `Pré-visualização — profissional ${index + 1}`,
          html: `Confira as ${dates.length} datas geradas.` + table,
          width: '720px',
          confirmButtonColor: '#780f5a',
          confirmButtonText: 'Ok',
        });
      }).catch((e) => Alert.toastError(e.response?.data ?? e));
    },
    validateBase() {
      if (!this.form.sched_patient_name) return Alert.toastError('Informe o paciente.'), false;
      if (!this.form.sched_patient_cpf && !this.form.document_number) return Alert.toastError('Informe o documento.'), false;
      if (!this.form.id_paymeth) return Alert.toastError('Informe a forma de pagamento.'), false;
      if (this.allowInsurance && !this.form.id_insurance) return Alert.toastError('Informe o convênio.'), false;
      if (!this.form.start_date) return Alert.toastError('Informe a data de início.'), false;
      return true;
    },
    validateSlot(slot, index) {
      if (!slot.id_prof || !slot.id_spec || !slot.first_date || !slot.sched_time || (slot.has_recurrence && !slot.tp_recurrence)) {
        Alert.toastError(`Complete os dados do profissional ${index + 1}.`);
        return false;
      }
      const paymentError = this.slotPaymentError(slot);
      if (paymentError) {
        Alert.toastError(paymentError);
        return false;
      }
      if (!moment(slot.first_date, 'DD/MM/YYYY', true).isValid()) {
        Alert.toastError(`Data da 1ª consulta inválida no profissional ${index + 1}.`);
        return false;
      }
      if (!slot.has_recurrence) return true;
      if (!slot.max_occurrences || Number(slot.max_occurrences) < 1) {
        Alert.toastError(`Informe a quantidade máxima de sessões do profissional ${index + 1}.`);
        return false;
      }
      if (slot.tp_recurrence === 'P' && slot.frequency === 'weekly' && (!slot.days_of_week || !slot.days_of_week.length)) {
        Alert.toastError(`Selecione os dias da semana do profissional ${index + 1}.`);
        return false;
      }
      return true;
    },
    payloadBase() {
      return {
        ...this.form,
        id_insurance: this.allowInsurance ? this.form.id_insurance : null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    },
    slotPayload(slot) {
      const recurrence = slot.has_recurrence
        ? {
          tp_recurrence: slot.tp_recurrence,
          frequency: slot.frequency,
          interval: slot.interval,
          days_of_week: slot.days_of_week,
          max_occurrences: slot.max_occurrences,
        }
        : {
          tp_recurrence: null,
          frequency: null,
          interval: null,
          days_of_week: [],
          max_occurrences: null,
        };

      return {
        id_prof: slot.id_prof,
        id_spec: slot.id_spec,
        first_date: slot.first_date,
        has_recurrence: slot.has_recurrence,
        ...recurrence,
        week_day: this.slotWeekDay(slot),
        sched_time: slot.sched_time,
        interval_minutes: slot.interval_minutes,
        id_avail: slot.id_avail,
        id_place_location: slot.id_place_location,
      };
    },
    submit() {
      if (this.readonly) return;
      if (!this.validateBase()) return;
      for (let i = 0; i < this.slots.length; i++) {
        if (!this.validateSlot(this.slots[i], i)) return;
      }

      this.submitting = true;
      http.post(`/places/${this.place.id_place}/schedules/programs`, {
        ...this.payloadBase(),
        slots: this.slots.map((s) => this.slotPayload(s)),
      }).then(() => {
        Alert.toastSuccess('Programa criado e agendamentos gerados.');
        this.$emit('saved');
      }).catch((e) => {
        Alert.toastError(e.response?.data ?? e);
      }).finally(() => {
        this.submitting = false;
      });
    },
    async printPreview() {
      if (!(this.readonly && this.programId)) {
        if (!this.validateBase()) return;
        for (let i = 0; i < this.slots.length; i++) {
          if (!this.validateSlot(this.slots[i], i)) return;
        }
      }

      Loader.show('Gerando PDF', 'Aguarde...');
      try {
        let response;
        if (this.readonly && this.programId) {
          response = await http.get(
            `/places/${this.place.id_place}/schedules/programs/${this.programId}/pdf`,
            { params: { inline: true }, responseType: 'blob' },
          );
        } else {
          response = await http.post(
            `/places/${this.place.id_place}/schedules/programs/preview-pdf`,
            {
              ...this.payloadBase(),
              slots: this.slots.map((s) => this.slotPayload(s)),
              inline: true,
            },
            { responseType: 'blob' },
          );
        }
        Download.openPdf(response.data);
      } catch (e) {
        Alert.toastError('Não foi possível gerar o PDF do programa.');
      } finally {
        Loader.close();
      }
    },
  },
};
</script>

<style>
.program-slot-card {
  background: #fafafa;
  overflow: visible;
}

.program-slot-calendar {
  display: flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  overflow: visible;
}

.program-slot-calendar :deep(.p-inputtext) {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}

#kt_modal_event .p-dropdown,
#kt_modal_event .p-calendar {
  overflow: visible;
}

#kt_modal_event .p-overlay-open,
#kt_modal_event .p-calendar.p-focus {
  position: relative;
  z-index: 20;
}

#kt_modal_event .p-dropdown-panel,
#kt_modal_event .p-datepicker {
  z-index: 1100 !important;
}
</style>

<style scoped>
.program-slot-card .input-group .program-slot-preview {
  min-width: 2.6rem;
  padding: .45rem .65rem;
  border-color: #780f5a;
  background: #f8f5f7;
  color: #780f5a;
}
.program-slot-card .input-group .program-slot-preview i { color: inherit; font-size: 1rem; }
.program-slot-card .input-group .program-slot-preview:hover { background: #780f5a; color: #fff; }
.program-slot-card .input-group .program-slot-preview:focus-visible { outline: 2px solid #780f5a; outline-offset: 2px; }
</style>
